// ============================================
// Nebula and Galaxy Rendering
// Three.js nebula and galaxy objects for Deep Space stage
// ============================================

import type { Group, ShaderMesh, NebulaPatternConfig } from './types';
import { CONFIG } from '../../../config/game-config';
import { getP5 } from '../../../core/p5-instance';
import { addToScene, removeFromScene } from './scene';
import { getDt } from '../../../store/index';
import {
    commonVertexShader,
    giantNebulaFragmentShader,
    globularClusterFragmentShader,
    spiralGalaxyFragmentShader,
    andromedaGalaxyFragmentShader
} from './shaders';

// Nebula objects
let threeNebulaGroup: Group | null = null;
let threeNebulaMeshes: ShaderMesh[] = [];

/**
 * Get the nebula group
 */
export function getThreeNebulaGroup(): Group | null {
    return threeNebulaGroup;
}

/**
 * Create nebula and galaxy objects for Deep Space stage
 */
export function createThreeNebula(): void {
    threeNebulaGroup = new THREE.Group();
    threeNebulaGroup.position.z = -450;
    threeNebulaGroup.visible = false;

    // Galaxy Cluster pattern configuration
    const pattern: NebulaPatternConfig = {
        name: 'Galaxy Cluster',
        giantNebulae: [
            // Slight purple/magenta accent
            { color1: [0.06, 0.04, 0.10], color2: [0.12, 0.08, 0.20], color3: [0.03, 0.02, 0.06], size: 900, x: 0.5, y: 0.5 }
        ],
        globularClusters: [
            // Warm gold tone (bottom-left)
            { coreColor: [0.92, 0.88, 0.70], outerColor: [0.65, 0.58, 0.42], size: 45, x: 0.15, y: 0.75 }
        ],
        andromedaGalaxies: [
            // Andromeda galaxy with emerald/teal arms (top-right)
            { coreColor: [0.90, 0.85, 0.75], armColor: [0.25, 0.60, 0.55], dustColor: [0.20, 0.35, 0.38], size: 360, x: 0.72, y: 0.28 }
        ],
        galaxies: [
            // Central spiral galaxy: gold core + blue-purple accent
            { coreColor: [0.88, 0.82, 0.65], armColor: [0.40, 0.45, 0.68], dustColor: [0.22, 0.28, 0.42], size: 210, x: 0.48, y: 0.50, tilt: 1.8 },
            // Bottom-right spiral galaxy: blue-white core + pink arms
            { coreColor: [0.85, 0.92, 0.98], armColor: [0.70, 0.30, 0.60], dustColor: [0.25, 0.15, 0.35], size: 260, x: 0.78, y: 0.82, tilt: 1.3 }
        ]
    };

    buildNebulaPattern(pattern);

    addToScene(threeNebulaGroup);
}

/**
 * Build nebula meshes from pattern configuration
 */
export function buildNebulaPattern(pattern: NebulaPatternConfig): void {
    if (!threeNebulaGroup) {
        return;
    }

    // Clear existing meshes
    while (threeNebulaGroup.children.length > 0) {
        const child = threeNebulaGroup.children[0] as ShaderMesh;
        threeNebulaGroup.remove(child);
        if (child.geometry) {
            child.geometry.dispose();
        }
        if (child.material) {
            child.material.dispose();
        }
    }
    threeNebulaMeshes = [];

    // Create giant nebulae
    if (pattern.giantNebulae) {
        pattern.giantNebulae.forEach((cfg, i) => {
            const geometry = new THREE.PlaneGeometry(cfg.size, cfg.size * 0.6);
            const material = new THREE.ShaderMaterial({
                uniforms: {
                    time: { value: 0 },
                    color1: { value: new THREE.Vector3(cfg.color1[0], cfg.color1[1], cfg.color1[2]) },
                    color2: { value: new THREE.Vector3(cfg.color2[0], cfg.color2[1], cfg.color2[2]) },
                    color3: { value: new THREE.Vector3(cfg.color3[0], cfg.color3[1], cfg.color3[2]) },
                    alpha: { value: 0.9 },
                    seed: { value: i * 1.8 + 2.1 }
                },
                vertexShader: commonVertexShader,
                fragmentShader: giantNebulaFragmentShader,
                transparent: true,
                blending: THREE.AdditiveBlending,
                depthWrite: false
            });
            const mesh = new THREE.Mesh(geometry, material) as unknown as ShaderMesh;
            const threeX = cfg.x * CONFIG.width - CONFIG.width / 2;
            const threeY = CONFIG.height / 2 - cfg.y * CONFIG.height;
            mesh.position.set(threeX, threeY, -25);
            mesh.userData = { type: 'giantNebula', baseX: threeX, baseY: threeY, speed: 0.01, offset: i * 1.2, size: cfg.size };
            threeNebulaMeshes.push(mesh);
            threeNebulaGroup!.add(mesh);
        });
    }

    // Create spiral galaxies
    if (pattern.galaxies) {
        pattern.galaxies.forEach((cfg, i) => {
            const geometry = new THREE.PlaneGeometry(cfg.size, cfg.size);
            const material = new THREE.ShaderMaterial({
                uniforms: {
                    time: { value: 0 },
                    coreColor: { value: new THREE.Vector3(cfg.coreColor[0], cfg.coreColor[1], cfg.coreColor[2]) },
                    armColor: { value: new THREE.Vector3(cfg.armColor[0], cfg.armColor[1], cfg.armColor[2]) },
                    dustColor: { value: new THREE.Vector3(cfg.dustColor[0], cfg.dustColor[1], cfg.dustColor[2]) },
                    alpha: { value: 0.9 },
                    seed: { value: i * 3.1 + 1.2 },
                    tilt: { value: cfg.tilt },
                    arms: { value: cfg.arms ?? 2.0 }
                },
                vertexShader: commonVertexShader,
                fragmentShader: spiralGalaxyFragmentShader,
                transparent: true,
                blending: THREE.AdditiveBlending,
                depthWrite: false
            });
            const mesh = new THREE.Mesh(geometry, material) as unknown as ShaderMesh;
            const threeX = cfg.x * CONFIG.width - CONFIG.width / 2;
            const threeY = CONFIG.height / 2 - cfg.y * CONFIG.height;
            mesh.position.set(threeX, threeY, -10 + i * 2);
            mesh.rotation.z = i * 0.8 + 0.3;
            mesh.userData = { type: 'galaxy', baseX: threeX, baseY: threeY, speed: 0.015 + i * 0.005, offset: i * 2.0, size: cfg.size };
            threeNebulaMeshes.push(mesh);
            threeNebulaGroup!.add(mesh);
        });
    }

    // Create Andromeda-style galaxies
    if (pattern.andromedaGalaxies) {
        pattern.andromedaGalaxies.forEach((cfg, i) => {
            const geometry = new THREE.PlaneGeometry(cfg.size, cfg.size * 0.3);
            const material = new THREE.ShaderMaterial({
                uniforms: {
                    time: { value: 0 },
                    coreColor: { value: new THREE.Vector3(cfg.coreColor[0], cfg.coreColor[1], cfg.coreColor[2]) },
                    armColor: { value: new THREE.Vector3(cfg.armColor[0], cfg.armColor[1], cfg.armColor[2]) },
                    dustColor: { value: new THREE.Vector3(cfg.dustColor[0], cfg.dustColor[1], cfg.dustColor[2]) },
                    alpha: { value: 0.9 },
                    seed: { value: i * 2.5 + 1.8 }
                },
                vertexShader: commonVertexShader,
                fragmentShader: andromedaGalaxyFragmentShader,
                transparent: true,
                blending: THREE.AdditiveBlending,
                depthWrite: false
            });
            const mesh = new THREE.Mesh(geometry, material) as unknown as ShaderMesh;
            const threeX = cfg.x * CONFIG.width - CONFIG.width / 2;
            const threeY = CONFIG.height / 2 - cfg.y * CONFIG.height;
            mesh.position.set(threeX, threeY, -12);
            mesh.userData = { type: 'andromedaGalaxy', baseX: threeX, baseY: threeY, speed: 0.008, offset: i * 1.5, size: cfg.size };
            threeNebulaMeshes.push(mesh);
            threeNebulaGroup!.add(mesh);
        });
    }

    // Create globular clusters
    if (pattern.globularClusters) {
        pattern.globularClusters.forEach((cfg, i) => {
            const geometry = new THREE.PlaneGeometry(cfg.size, cfg.size);
            const material = new THREE.ShaderMaterial({
                uniforms: {
                    time: { value: 0 },
                    coreColor: { value: new THREE.Vector3(cfg.coreColor[0], cfg.coreColor[1], cfg.coreColor[2]) },
                    outerColor: { value: new THREE.Vector3(cfg.outerColor[0], cfg.outerColor[1], cfg.outerColor[2]) },
                    alpha: { value: 0.85 },
                    seed: { value: i * 2.8 + 1.5 }
                },
                vertexShader: commonVertexShader,
                fragmentShader: globularClusterFragmentShader,
                transparent: true,
                blending: THREE.AdditiveBlending,
                depthWrite: false
            });
            const mesh = new THREE.Mesh(geometry, material) as unknown as ShaderMesh;
            const threeX = cfg.x * CONFIG.width - CONFIG.width / 2;
            const threeY = CONFIG.height / 2 - cfg.y * CONFIG.height;
            mesh.position.set(threeX, threeY, -9 + i * 2);
            mesh.userData = { type: 'globularCluster', baseX: threeX, baseY: threeY, speed: 0.012 + i * 0.004, offset: i * 2.2, size: cfg.size };
            threeNebulaMeshes.push(mesh);
            threeNebulaGroup!.add(mesh);
        });
    }
}

/**
 * Update nebula visibility and animation
 */
export function updateThreeNebula(showNebula: boolean, shakeX: number): void {
    if (!threeNebulaGroup) {
        return;
    }

    threeNebulaGroup.visible = showNebula;

    if (showNebula) {
        const p = getP5();
        const timeValue = p.frameCount * 0.016;
        const dt = getDt();

        for (const mesh of threeNebulaMeshes) {
            // Update shader time uniform
            if (mesh.material.uniforms.time) {
                mesh.material.uniforms.time.value = timeValue;
            }

            // Slow downward movement
            const ud = mesh.userData;
            mesh.position.y -= ud.speed * dt;

            // Gentle wobble
            const wobbleX = Math.sin(timeValue * 0.3 + ud.offset) * 20;
            mesh.position.x = ud.baseX + wobbleX + shakeX;

            // Wrap around when off screen
            if (mesh.position.y < -CONFIG.height / 2 - ud.size) {
                mesh.position.y = CONFIG.height / 2 + ud.size * 0.5;
                ud.baseX = (Math.random() * 0.8 + 0.1) * CONFIG.width - CONFIG.width / 2;
            }
        }
    }
}

/**
 * Dispose of nebula resources
 */
export function disposeThreeNebula(): void {
    if (threeNebulaGroup) {
        // Dispose all meshes
        for (const mesh of threeNebulaMeshes) {
            if (mesh.geometry) {
                mesh.geometry.dispose();
            }
            if (mesh.material) {
                mesh.material.dispose();
            }
        }
        threeNebulaMeshes = [];

        removeFromScene(threeNebulaGroup);
        threeNebulaGroup = null;
    }
}
